import { useEffect, useState } from 'react';

/* Gap between two players being allowed to mount, in milliseconds. */
const STAGGER_MS = 150;

/*
 * The next moment a slot is free. It lives at module scope on purpose: the queue has to be shared
 * by every card on the page, which is the whole point — a per-card timer would let them all start
 * at once, which is what this exists to prevent.
 */
let nextSlot = 0;

/*
 * Hands out one slot per STAGGER_MS and returns the cancel for it. When claims are spread out in
 * time each one is granted immediately, because the cursor has already fallen behind `now`; only
 * simultaneous claims queue up. So scrolling past live cards one at a time costs nothing.
 */
const claimSlot = (grant: () => void): (() => void) => {
  const now = Date.now();
  const at = Math.max(now, nextSlot);

  nextSlot = at + STAGGER_MS;

  const timer = setTimeout(grant, at - now);

  return (): void => {
    clearTimeout(timer);
  };
};

/*
 * Spreads out the mounting of the card players, across the event grid and the sidebar alike.
 * Each one is a full video.js instance with two
 * plugins: a control bar built in the DOM, plugins registered, an HLS connection opened. When
 * a batch of events goes live together every visible card used to do all of that in the same frame,
 * on the main thread. That burst is what froze the page.
 *
 * The delay is invisible in practice: a stream takes far longer than this to paint its first frame.
 */
export const useStaggeredMount = (ready: boolean): boolean => {
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    if (!ready) {
      setGranted(false);

      return undefined;
    }

    return claimSlot(() => {
      setGranted(true);
    });
  }, [ready]);

  return granted;
};
